import React from 'react';
import {
  Box,
  VStack,
  Button,
  Divider,
  Tooltip,
  Text,
  HStack,
  IconButton
} from '@chakra-ui/react';
import {
  FiPlay,
  FiCheckCircle,
  FiSend,
  FiDatabase,
  FiZap,
  FiClock,
  FiUser,
  FiGitBranch,
  FiMail,
  FiMousePointer,
  FiPhone,
  FiHash,
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
  FiGitMerge,
  FiShield,
  FiToggleLeft
} from 'react-icons/fi';
import { BsWhatsapp } from 'react-icons/bs';
import {  } from 'react-icons/lu';
import {  } from 'react-icons/ci';
import { FaUserCog } from 'react-icons/fa';
import { RiListOrdered2 } from 'react-icons/ri';

export default function ComponentLibrary({ onAddNode, node, isEdit, isMinimized, onToggle }) {
  if (isMinimized) {
    return (
      <Box
        w="40px"
        bg="white"
        borderRight="1px"
        borderColor="gray.200"
        display="flex"
        flexDirection="column"
        alignItems="center"
        pt={3}
        flexShrink={0}
      >
        <Tooltip label="Expand panel" placement="right">
          <IconButton
            size="xs"
            variant="ghost"
            icon={<FiChevronRight />}
            onClick={onToggle}
            aria-label="Expand panel"
          />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box w="250px" bg="white" borderRight="1px" borderColor="gray.200" overflowY="auto" flexShrink={0}>
      <Box display="flex" justifyContent="flex-end" px={2} pt={2}>
        <Tooltip label="Collapse panel" placement="right">
          <IconButton
            size="xs"
            variant="ghost"
            icon={<FiChevronLeft />}
            onClick={onToggle}
            aria-label="Collapse panel"
          />
        </Tooltip>
      </Box>
      <VStack spacing={4} p={4} align="stretch">
        {/* Flow Control */}
        <Box>
          <Text fontSize="md" fontWeight={"500"} color="black" mb={3}>
            Flow Control
          </Text>
          <VStack spacing={2} align="stretch">
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiPlay />}
              justifyContent="flex-start"
              onClick={() => onAddNode('start')}
              bgGradient="linear(to-r, #ecfdf5, #f0fdf4)"
              border={"1px solid #b9f8cf"}
              _hover={{ bg: '#d0fae5 !important', color: "black !important" }}
            >
              Start
            </Button>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiCheckCircle />}
              justifyContent="flex-start"
              onClick={() => onAddNode('end')}
              bgGradient="linear(to-r, #fcf9fa, #fbf9fa)"
              border={"1px solid #d1d5dc"}
              _hover={{ bg: '#f9f3f4 !important', color: "black !important" }}
            >
              End
            </Button>
          
            <Tooltip label="Combine multiple branches into a single path" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiGitMerge />}
                justifyContent="flex-start"
                onClick={() => onAddNode('merge')}
                bgGradient="linear(to-r, #faf5ff, var(--brand-50))"
                border={"1px solid #d8b4fe"}
                color={"#7c3aed"}
                _hover={{ bg: '#ede9fe !important', color: "black !important" }}
              >
                Merge
              </Button>
            </Tooltip>

            <Tooltip label="Send multiple messages in a single sequence node" placement="right">
              <Button
                size="sm"
                variant="outline"
                justifyContent="flex-start"
                onClick={() => onAddNode('multiStep')}
                bgGradient="linear(to-r, var(--brand-50), #f5f3ff)"
                border={"1px solid var(--brand-200)"}
                color={"var(--brand-primary)"}
                _hover={{ bg: 'var(--brand-100) !important', color: "black !important" }}
              >
                <HStack spacing={2} w="full">
                  <RiListOrdered2 size={14} />
                  <Text fontSize="sm" flex={1} textAlign="left">Multi-Step</Text>
                </HStack>
              </Button>
            </Tooltip>
          </VStack>
        </Box>

        <Divider />

        {/* Messaging */}
        <Box>
          <Text fontSize="md" fontWeight={"500"} color="black" mb={3}>
            Messaging
          </Text>
          <VStack spacing={2} align="stretch">
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiSend />}
              justifyContent="flex-start"
              onClick={() => onAddNode('action')}
              bgGradient="linear(to-r, #f0f9ff, var(--brand-tint))"
              border={"1px solid var(--brand-tint-strong)"}
              color={"#155dfc"}
              _hover={{ bg: '#dff2fe !important', color: "black !important" }}
            >
              Message
            </Button>
              <Tooltip label="Trigger another flow as a sub-routine" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiGitMerge />}
                justifyContent="flex-start"
                onClick={() => onAddNode('callFlowNode')}
                bgGradient="linear(to-r, #f0fdfa, #ecfeff)"
                border={"1px solid #99f6e4"}
                color={"#0d9488"}
                _hover={{ bg: '#ccfbf1 !important', color: "black !important" }}
              >
                Call xflow
              </Button>
            </Tooltip>
            
            <Tooltip label="Initiate an IVR call with retry logic" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiPhone />}
                justifyContent="flex-start"
                onClick={() => onAddNode('ivrNode')}
                bgGradient="linear(to-r, #ecfeff, var(--brand-tint))"
                border={"1px solid #a2f4fd"}
                color={"#0092b8"}
                _hover={{ bg: 'var(--brand-tint-strong) !important', color: "black !important" }}
              >
                IVR Call
              </Button>
            </Tooltip>
            <Tooltip label="Send a WhatsApp Flow to the contact" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<BsWhatsapp />}
                justifyContent="flex-start"
                onClick={() => onAddNode('callWhatsappFlowNode')}
                bgGradient="linear(to-r, #f0fdf4, #dcfce7)"
                border={"1px solid #86efac"}
                color={"#16a34a"}
                _hover={{ bg: '#bbf7d0 !important', color: "black !important" }}
              >
                WhatsApp Flow
              </Button>
            </Tooltip>
            <Tooltip label="Wait for and route inbound SMS responses" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiMessageSquare />}
                justifyContent="flex-start"
                onClick={() => onAddNode('smsInboundNode')}
                bgGradient="linear(to-r, #ecfdf5, #f0fdf4)"
                border={"1px solid #6ee7b7"}
                color={"#059669"}
                _hover={{ bg: '#d1fae5 !important', color: "black !important" }}
              >
                SMS Inbound
              </Button>
            </Tooltip>
          </VStack>
        </Box>

        <Divider />

        {/* Actions */}
        <Box>
          <Text fontSize="md" fontWeight={"500"} size="xs" color="black" mb={3}>
            Actions
          </Text>
          <VStack spacing={2} align="stretch">
            <Tooltip label="Update customer profile attributes" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiUser />}
                justifyContent="flex-start"
                onClick={() => onAddNode('updateProfile')}
                bgGradient="linear(to-r, #fff1f2, #fdf2f8)"
                border={"1px solid #f0c2c2"}
                color={"#c53030"}
                _hover={{ bg: '#f8e6e6 !important', color: "black !important" }}
              >
                Update Profile
              </Button>
            </Tooltip>
            <Tooltip label="Make HTTP requests to external systems" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiDatabase />}
                justifyContent="flex-start"
                onClick={() => onAddNode('api')}
                bgGradient="linear(to-r, var(--brand-50), #faf5ff)"
                border={"1px solid #e9d4ff"}
                color={"#9810fa"}
                _hover={{ bg: 'var(--brand-100) !important', color: "black !important" }}
              >
                API Call
              </Button>
            </Tooltip>
            <Tooltip label="Split traffic between multiple variants" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiGitBranch />}
                justifyContent="flex-start"
                onClick={() => onAddNode('abTest')}
                bgGradient="linear(to-r, #faf5ff, var(--brand-50))"
                border={"1px solid #e9d4ff"}
                color={"#4f39f6"}
                _hover={{ bg: 'var(--brand-100) !important', color: "black !important" }}
              >
                A/B Test
              </Button>
            </Tooltip>
           
            
          </VStack>
        </Box>

        <Divider />

        {/* Consent */}
        <Box>
          <Text fontSize="md" fontWeight={"500"} color="black" mb={3}>
            Consent
          </Text>
          <VStack spacing={2} align="stretch">
            <Tooltip label="Branch on the contact's opt-in status for a channel" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiShield />}
                justifyContent="flex-start"
                onClick={() => onAddNode('checkChannelConsent')}
                bgGradient="linear(to-r, #f5f3ff, #faf5ff)"
                border={"1px solid #ddd6ff"}
                color={"#7c3aed"}
                _hover={{ bg: '#ede9fe !important', color: "black !important" }}
              >
                Check Channel Consent
              </Button>
            </Tooltip>

            <Tooltip label="Opt the contact in or out of a channel" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiToggleLeft />}
                justifyContent="flex-start"
                onClick={() => onAddNode('updateChannelConsent')}
                bgGradient="linear(to-r, #fdf4ff, #faf5ff)"
                border={"1px solid #f5d0fe"}
                color={"#c026d3"}
                _hover={{ bg: '#fae8ff !important', color: "black !important" }}
              >
                Update Channel Consent
              </Button>
            </Tooltip>
          </VStack>
        </Box>

        <Divider />

        {/* Logic Components */}
        <Box>
          <Text fontSize="md" fontWeight={"500"} color="black" mb={3}>
            Logic Components
          </Text>
          <VStack spacing={2} align="stretch">
            <Tooltip label="Check if a specific event has occurred" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiZap />}
                justifyContent="flex-start"
                onClick={() => onAddNode('condition')}
                bgGradient="linear(to-r, #fffbeb, #fff7ed)"
                border={"1px solid #ffd7a8"}
                color={"#f54a00"}
                _hover={{ bg: '#fef3c6 !important', color: "black !important" }}
              >
                Event Check
              </Button>
            </Tooltip>

            {/* <Tooltip
              label="Advanced condition with conversion, profile & journey checks"
              placement="right"
            >
              <Button
                size="sm"
                variant="outline"
                justifyContent="flex-start"
                onClick={() => onAddNode('enhancedConditionNode')}
              >
                <HStack spacing={2} w="full">
                  <FiGitBranch />
                  <Text fontSize="sm" flex={1} textAlign="left">
                    Enhanced Condition
                  </Text>
                  <Badge
                    fontSize="8px"
                    px={1.5}
                    py={0.5}
                    colorScheme="orange"
                    variant="subtle"
                  >
                    Advanced
                  </Badge>
                </HStack>
              </Button>
            </Tooltip> */}

            <Tooltip label="Route flow through multi-group OR/AND conditions" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiGitBranch />}
                justifyContent="flex-start"
                onClick={() => onAddNode('smartConditionNode')}
                bgGradient="linear(to-r, #faf5ff, #f5f3ff)"
                border={"1px solid #d8b4fe"}
                color={"#7c3aed"}
                _hover={{ bg: '#ede9fe !important', color: "black !important" }}
              >
                Smart Condition
              </Button>
            </Tooltip>

            <Tooltip label="Handle DTMF keypad responses from IVR" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiHash />}
                justifyContent="flex-start"
                onClick={() => onAddNode('dtmfNode')}
                bgGradient="linear(to-r, #faf5ff, #f5f3ff)"
                border={"1px solid #ddd6ff"}
                color={"#7f22fe"}
                _hover={{ bg: '#f3e8ff !important', color: "black !important" }}
              >
                DTMF Response
              </Button>
            </Tooltip>

            <Tooltip
              label="Branch flow based on customer profile attributes"
              placement="right"
            >
              <Button
                size="sm"
                variant="outline"
                justifyContent="flex-start"
                onClick={() => onAddNode('customerProfile')}
                bgGradient="linear(to-r, #fff1f2, #fdf2f8)"
                border={"1px solid #fccee8"}
                color={"#e60076"}
                _hover={{ bg: '#ffe4e6 !important', color: "black !important" }}
              >
                <HStack spacing={2} w="full">
                  <FaUserCog size={14} />
                  <Text fontSize="sm" flex={1} textAlign="left">
                    Check Customer Profile
                  </Text>
                </HStack>
              </Button>
            </Tooltip>
            {/* <Tooltip label="Wait for and route inbound SMS responses" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiMessageSquare />}
                justifyContent="flex-start"
                onClick={() => onAddNode('smsInboundNode')}
                bgGradient="linear(to-r, #ecfdf5, #f0fdf4)"
                border={"1px solid #6ee7b7"}
                color={"#059669"}
                _hover={{ bg: '#d1fae5 !important', color: "black !important" }}
              >
                SMS Inbound
              </Button>
            </Tooltip> */}
             <Tooltip label="Check if message was read or delivered" placement="right">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiMail />}
                justifyContent="flex-start"
                onClick={() => onAddNode('messageRead')}
                bgGradient="linear(to-r, #f0fdf4, #ecfdf5)"
                border={"1px solid #a2f4fd"}
                color={"#009966"}
                _hover={{ bg: '#a4f4cf !important', color: "black !important" }}
              >
                Message Delivery Status
              </Button>
            </Tooltip>
            <Tooltip label="Track button click interactions" placement="right">
              <Button
                leftIcon={<FiMousePointer />}
                size="sm"
                variant="outline"
                onClick={() => onAddNode('buttonClicked')}
                w="full"
                justifyContent="flex-start"
                bgGradient="linear(to-r, #fff1f2, #fdf2f8)"
                border={"1px solid #f0c2c2"}
                color={"#ec003f"}
                _hover={{ bg: '#f8e6e6 !important', color: "black !important" }}
              >
                Button Clicked
              </Button>
            </Tooltip>
          </VStack>
        </Box>

        <Divider />

        {/* Delays */}
        <Box>
          <Text fontSize="md" fontWeight={"500"} color="black" mb={3}>
            Delays
          </Text>
          <VStack spacing={2} align="stretch">
            {/* <Button
              size="sm"
              variant="outline"
              leftIcon={<FiCalendar />}
              justifyContent="flex-start"
              onClick={() => onAddNode('delay')}

              bgGradient="linear(to-r, #fffbeb, #fff7ed)"
              border={"1px solid #ffd7a8"}
              color={"#f54a00"}
              _hover={{ bg: '#fef3c6 !important', color: "black !important" }}
            >
              Wait for Event
            </Button> */}

            <Tooltip
              label="Dynamic wait adapts per execution without creating multiple flows"
              placement="right"
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAddNode('delay', 'dynamic')}
                w="full"
                justifyContent="flex-start"
                bgGradient="linear(to-r, #f0fdf4, #ecfdf5)"
                border={"1px solid #a2f4fd"}
                color={"#009966"}
                _hover={{ bg: '#a4f4cf !important', color: "black !important" }}
              >
                <HStack spacing={2} w="full">
                  <Box position="relative">
                    <FiClock />
                    <Box position="absolute" bottom="-2px" right="-2px">
                      <FiZap size={10} color="#FBAF3F" />
                    </Box>
                  </Box>
                  <Text fontSize="sm" flex={1} textAlign="left">
                    Wait Time (Dynamic)
                  </Text>
                  {/* <Badge 
                    fontSize="8px" 
                    px={1.5} 
                    py={0.5} 
                    colorScheme="green"
                    variant="subtle"
                  >
                    Advanced
                  </Badge> */}
                </HStack>
              </Button>
            </Tooltip>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}